import { DefaultValues } from 'react-hook-form'
import { array, boolean, InferType, lazy, mixed, number, object, string } from 'yup'

import { modificationModalSchema } from './components/VehicleModificationsField/AddModificationForm'
import {
  isAcceptedRegisteredKeeperValue,
  showLegalOwnerQuestion,
} from 'directFlow/pages/Vehicle/utils/utils'
import { SignageType } from 'directFlow/state'
import { requiredMessage } from 'directFlow/state/config/content'
import {
  purchaseDateValidationMessages,
  purchaseDateValidator,
} from 'directFlow/state/formManager/purchaseDateValidator/purchaseDateValidator'

import {
  Channel,
  OtherVehicleUse,
  OvernightLocation,
  VehicleFoundValues,
  VehicleOwnerType,
  VehicleType,
} from 'types'

export const vehiclePageDefaultValues = {
  purchased: true,
  purchaseDate: {
    month: '',
    year: '',
  },
  userInputtedGrossWeight: undefined,
  modifications: {
    checked: undefined,
    list: [],
  },
  signage: undefined,
  internalRacking: undefined,
  trailer: undefined,
  refrigerationUnit: undefined,
  tippingUnit: undefined,
  visitsHazardousSites: undefined,
  carriesHazardousGoods: undefined,
  badDriverHotline: undefined,
  isRegisteredKeeper: undefined,
  registeredKeeper: undefined,
  isLegalOwner: undefined,
  legalOwner: undefined,
  drivesOtherCars: undefined,
  otherVehicleYears: undefined,
  useOfOtherVehicle: undefined,
  overnightLocation: undefined,
  mileage: 10000,
  totalCarsInHousehold: undefined,
} satisfies DefaultValues<VehicleSchemaType>

export const vehiclePageSchema = object({
  purchased: boolean().required(requiredMessage),
  purchaseDate: object({
    month: string(),
    year: string(),
  }).when('purchased', {
    is: true,
    then: () =>
      object({
        month: string(),
        year: string(),
      })
        .test({
          name: 'purchase-date-validation',
          test: (purchaseDate, context) => {
            const res = purchaseDateValidator(purchaseDate)

            if ('error' in res) {
              return context.createError({
                type: res.error,
                message: purchaseDateValidationMessages[res.error],
              })
            }

            return true
          },
        })
        .required(requiredMessage),
    otherwise: () =>
      object({
        month: string(),
        year: string(),
      }).optional(),
  }),
  modifications: object({
    checked: boolean().nullable().required(requiredMessage),
    list: array()
      .of(lazy(() => modificationModalSchema))
      .when('checked', {
        is: true,
        then: (schema) => schema.min(1, 'Please add at least one item or select "No"'),
        otherwise: (schema) => schema.optional().default([]),
      })
      .optional()
      .default([]),
  }),
  userInputtedGrossWeight: number()
    .nullable()
    .when(['$vehicleFound'], {
      is: (vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN && vehicleFound.grossWeight === null
      },
      then: () => number().required(),
      otherwise: () => number().nullable(),
    }),
  signage: mixed<SignageType>()
    .nullable()
    .when(['$vehicleFound'], {
      is: (vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN
      },
      then: (schema) => schema.required(requiredMessage),
      otherwise: (schema) => schema,
    }),
  internalRacking: boolean()
    .nullable()
    .when(['$vehicleFound'], {
      is: (vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN
      },
      then: () => boolean().required(requiredMessage),
      otherwise: () => boolean().nullable(),
    })
    .nullable()
    .default(null),
  trailer: boolean()
    .when(['$vehicleFound'], {
      is: (vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN
      },
      then: () => boolean().required(requiredMessage),
      otherwise: () => boolean().nullable(),
    })
    .nullable()
    .default(null),
  refrigerationUnit: boolean()
    .nullable()
    .when(['$vehicleFound'], {
      is: (vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN
      },
      then: () => boolean().required(requiredMessage),
      otherwise: () => boolean().nullable(),
    })
    .nullable()
    .default(null),
  tippingUnit: boolean()
    .nullable()
    .when(['$vehicleFound'], {
      is: (vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN
      },
      then: () => boolean().required(requiredMessage),
      otherwise: () => boolean().nullable(),
    })
    .nullable()
    .default(null),
  visitsHazardousSites: boolean()
    .nullable()
    .when(['$vehicleFound'], {
      is: (vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN
      },
      then: () => boolean().required(requiredMessage),
      otherwise: () => boolean().nullable(),
    })
    .nullable()
    .default(null),
  carriesHazardousGoods: boolean()
    .when(['$vehicleFound'], {
      is: (vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN
      },
      then: () => boolean().required(requiredMessage),
      otherwise: () => boolean().nullable(),
    })
    .nullable()
    .default(null),
  badDriverHotline: boolean()
    .nullable()
    .when(['$vehicleFound'], {
      is: (vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN
      },
      then: () => boolean().required(requiredMessage),
      otherwise: () => boolean().nullable(),
    })
    .nullable()
    .default(null),
  isRegisteredKeeper: boolean().nullable().required(requiredMessage),
  registeredKeeper: mixed<VehicleOwnerType>()
    .nullable()
    .when('isRegisteredKeeper', {
      is: (isRegisteredKeeper: boolean) => !isRegisteredKeeper,
      then: (schema) =>
        schema
          .test({
            name: 'Is outdated owner type',
            message: requiredMessage,
            test: (registeredKeeper) =>
              isAcceptedRegisteredKeeperValue({
                registeredKeeper: registeredKeeper,
              }),
          })
          .required(requiredMessage),
      otherwise: (schema) => schema,
    }),
  isLegalOwner: boolean()
    .when(['$vehicleFound', '$channel'], {
      is: (vehicleFound: VehicleFoundValues | undefined, channel: Channel | undefined) =>
        showLegalOwnerQuestion({
          vehicleType: vehicleFound?.vehicleType,
          channel,
        }),
      then: () => boolean().required(requiredMessage),
      otherwise: () => boolean().nullable(),
    })
    .nullable()
    .default(null),
  legalOwner: mixed<VehicleOwnerType>()
    .nullable()
    .when('isLegalOwner', {
      is: (isLegalOwner: boolean | undefined) => isLegalOwner === false,
      then: (schema) => schema.required(requiredMessage),
      otherwise: (schema) => schema,
    }),
  drivesOtherCars: boolean().nullable().required(requiredMessage),
  otherVehicleYears: number()
    .nullable()
    .when(['drivesOtherCars', '$vehicleFound'], {
      is: (drivesOtherCars: boolean, vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN && drivesOtherCars
      },
      then: () => number().required(requiredMessage),
      otherwise: () => number().nullable(),
    }),
  useOfOtherVehicle: mixed<OtherVehicleUse>()
    .nullable()
    .when(['drivesOtherCars', '$vehicleFound'], {
      is: (drivesOtherCars: boolean, vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN && drivesOtherCars
      },
      then: (schema) => schema.required(requiredMessage),
      otherwise: (schema) => schema,
    }),
  overnightLocation: mixed<OvernightLocation>()
    .oneOf(Object.values(OvernightLocation))
    .required(requiredMessage),
  mileage: number().required(requiredMessage),
  totalCarsInHousehold: number()
    .nullable()
    .when(['$vehicleFound'], {
      is: (vehicleFound: VehicleFoundValues) => {
        return vehicleFound?.vehicleType === VehicleType.VAN
      },
      then: () => number().nullable(),
      otherwise: () => number().required(requiredMessage),
    }),
})

export type VehicleSchemaType = InferType<typeof vehiclePageSchema>
